import { Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { Menu } from "primeng/menu";
import { MenuItem } from "primeng/api";
import { MonitoringGoalSettingCardStatusEnum, MonitoringGoalSettingCardStatusEnumTypes, MonitoringGoalSettingProposalStatusEnum, MonitoringGoalSettingUserSummary, MonitoringGoalSettingUserSummaryUpdateStatusEnum, MonitoringGoalSettingUserSummaryUpdateStatusTypes } from "alloy-performance-engine-cl";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { TranslateService } from "src/app/util/translate.service";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { ApeStatusBadgeComponent, StatusBadgeKind } from "../ape-status-badge/ape-status-badge.component";
import { ApeAvatarComponent } from "../ape-avatar/ape-avatar.component";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { ApeMenuComponent } from "../ape-menu/ape-menu.component";
import { LoggerService } from "src/ancestors/logger.service";

/**
 * Tabella del monitoraggio Goal Setting: colonne Collaboratore, Manager,
 * Stato scheda (chip), Stato proposte (chip), Aggiornamenti (contatore o n.d.),
 * Azioni (menu con voci placeholder no-op).
 */
@Component({
  selector: "ape-admin-monitoring-goal-setting-users-table",
  templateUrl: "./ape-admin-monitoring-goal-setting-users-table.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    TableModule,
    TooltipModule,
    TranslatePipe,
    ApeButtonComponent,
    ApeStatusBadgeComponent,
    ApeAvatarComponent,
    ApeMenuComponent
  ]
})
export class ApeAdminMonitoringGoalSettingUsersTableComponent {
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  readonly UPDATE_STATUS_ND = MonitoringGoalSettingUserSummaryUpdateStatusTypes.ND;

  private logger = inject(LoggerService);

  private translateService = inject(TranslateService);

  @Input() usersData: MonitoringGoalSettingUserSummary[] = [];

  @Output() impersonateUser = new EventEmitter<MonitoringGoalSettingUserSummary>();

  @Output() impersonateManager = new EventEmitter<MonitoringGoalSettingUserSummary>();

  @Output() downloadPdf = new EventEmitter<MonitoringGoalSettingUserSummary>();

  /** Sollecito M4 al manager del collaboratore (singolo). */
  @Output() solicitManager = new EventEmitter<MonitoringGoalSettingUserSummary>();

  /** Sollecito M3 al collaboratore per le proprie proposte (singolo). */
  @Output() solicitUser = new EventEmitter<MonitoringGoalSettingUserSummary>();

  /** Sollecito M6 al manager: ricondivisione scheda nella fase di Aggiornamento (singolo). */
  @Output() solicitManagerUpdate = new EventEmitter<MonitoringGoalSettingUserSummary>();

  @ViewChild("apeMenu") apeMenu!: Menu;

  menuItems: MenuItem[] = [];

  /**
   * Mappa per la chip "Stato scheda" / "Stato proposte" verso le varianti supportate da ApeStatusBadge.
   */
  getStatusBadgeKind(status: string | undefined): StatusBadgeKind {
    switch (status) {
      case MonitoringGoalSettingCardStatusEnumTypes.SHARED:
        return "completed";
      case MonitoringGoalSettingCardStatusEnumTypes.TO_DO:
        return "in-progress";
      case MonitoringGoalSettingCardStatusEnumTypes.NOT_DONE:
        return "blocked";
      case MonitoringGoalSettingCardStatusEnumTypes.ND:
      default:
        return "disable";
    }
  }

  /**
   * Etichetta di una chip "Stato scheda".
   */
  getCardStatusLabel(status: MonitoringGoalSettingCardStatusEnum | undefined): string {
    if (!status) return this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.cardStatus.ND);
    return this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.cardStatus[status]);
  }

  /**
   * Etichetta di una chip "Stato proposte".
   */
  getProposalStatusLabel(status: MonitoringGoalSettingProposalStatusEnum | undefined): string {
    if (!status) return this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.proposalStatus.ND);
    return this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.proposalStatus[status]);
  }

  isUpdatesNd(updateStatus: MonitoringGoalSettingUserSummaryUpdateStatusEnum | undefined): boolean {
    return !updateStatus || updateStatus === MonitoringGoalSettingUserSummaryUpdateStatusTypes.ND;
  }

  /**
   * Apre il menu Azioni costruendo le voci secondo i flag della riga.
   * I command sono placeholder no-op: l'implementazione effettiva verrà aggiunta in una futura iterazione.
   */
  openMenu(event: Event, user: MonitoringGoalSettingUserSummary): void {
    const labels = TRANSLATION_KEYS.admin.goalSettingMonitoring.actions;
    this.menuItems = [
      {
        label: this.translateService.instant(labels.IMPERSONATE_USER),
        disabled: !user.canImpersonateUser,
        icon: "assets/icons/user.svg",
        command: () => {
          this.impersonateUser.emit(user);
        }
      },
      {
        label: this.translateService.instant(labels.IMPERSONATE_MANAGER),
        disabled: !user.canImpersonateManager,
        icon: "assets/icons/user.svg",
        command: () => {
          this.impersonateManager.emit(user);
        }
      },
      {
        label: this.translateService.instant(labels.DOWNLOAD_PDF),
        disabled: !user.canDownloadPdf,
        icon: "assets/icons/file-down.svg",
        command: () => {
          this.downloadPdf.emit(user);
        }
      },
      {
        label: this.translateService.instant(labels.SOLICIT_MANAGER),
        disabled: !user.canSolicitManager,
        icon: "assets/icons/mail.svg",
        command: () => {
          this.logger.info(`[GS monitoring] solicit manager for user ${user.userId}`);
          this.solicitManager.emit(user);
        }
      },
      {
        label: this.translateService.instant(labels.SOLICIT_USER),
        disabled: !user.canSolicitUser,
        icon: "assets/icons/mail.svg",
        command: () => {
          this.logger.info(`[GS monitoring] solicit user proposal for user ${user.userId}`);
          this.solicitUser.emit(user);
        }
      }
      /*
      * non richiesto in analisi, per ora commentiamo
      ,
      {
        label: this.translateService.instant(labels.SOLICIT_MANAGER_UPDATE),
        disabled: !user.canSolicitManagerUpdate,
        command: () => {
          this.logger.info(`[GS monitoring] solicit manager update for user ${user.userId}`);
          this.solicitManagerUpdate.emit(user);
        }
      }*/
    ];
    this.apeMenu.toggle(event);
  }
}
